import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    FormErrorMessage,
    Box,
    Tooltip,
    CloseButton,
    InputGroup,
    InputRightAddon,
    Select,
} from "@chakra-ui/react";
import { FunctionComponent, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { CreateOrUpdateBenchmarkInput, BenchmarkInput } from "../../../api/benchmark/benchmarkApi";
import { useCreateBenchmarkInput, useDeleteBenchmarkInput, useUpdateBenchmarkInput } from "../../../api/benchmark/benchmarkQueries";
import { ConfirmModal } from "../EntityPanel/ConfirmModal";
import { FileContainer, FileUploader } from "am-tax-fe-core";
import {
    ALLOWED_BENCHMARK_EXTENSIONS,
    getRegionLabel,
    getRegionValue,
    getProfitLevelIndicatorMethodLabel,
    ProfitLevelIndicatorMethod,
    Region,
    IQR_TYPES,
    IqrType,
    REGIONS,
    SERVICE_TYPES,
    PROFIT_LEVEL_INDICATOR_METHODS,
} from "../../../utils/constants";

interface BenchmarkFormProps {
    show: boolean;
    onClose: () => void;
    rowData?: BenchmarkInput;
    onSave?: (data: BenchmarkInput) => void;
    clientId?: string;
}

type FormFields = {
    region: string;
    serviceType: string;
    benchmarkFile: string;
    minimumAverageTotalRevenue: string;
    minimumThreeYearRevenue: string;
    minimumTwoYearRevenue: string;
    minimumOneYearRevenue: string;
    minimumInventory: string;
    minimumInventoryThreeYear: string;
    minimumInventoryTwoYear: string;
    minimumInventoryOneYear: string;
    minimumPPE: string;
    minimumPPEThreeYear: string;
    minimumPPETwoYear: string;
    minimumPPEOneYear: string;
    inventorySalesRatio: string;
    profitLevelIndicatorMethod: ProfitLevelIndicatorMethod;
    iqrType: IqrType;
};

export const BenchmarkForm: FunctionComponent<BenchmarkFormProps> = ({ onClose, rowData, clientId }) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [hasBenchmarkFile, setHasBenchmarkFile] = useState<boolean>(!!rowData?.blobStorageLink && rowData?.blobStorageLink?.split("/").length > 1);
    const [selectedBenchmarkFile, setSelectedBenchmarkFile] = useState<FileContainer | undefined>();
    const isEdit: boolean = !!rowData;

    const formValues: FormFields = {
        region: rowData?.region ? getRegionValue(rowData.region) ?? rowData.region : "",
        serviceType: rowData?.serviceType || "",
        benchmarkFile: rowData?.blobStorageLink || "",
        minimumAverageTotalRevenue: rowData?.minimumAverageTotalRevenue || "",
        minimumThreeYearRevenue: rowData?.minimumThreeYearRevenue || "",
        minimumTwoYearRevenue: rowData?.minimumTwoYearRevenue || "",
        minimumOneYearRevenue: rowData?.minimumOneYearRevenue || "",
        minimumInventory: rowData?.minimumInventory || "",
        minimumInventoryThreeYear: rowData?.minimumInventoryThreeYear || "",
        minimumInventoryTwoYear: rowData?.minimumInventoryTwoYear || "",
        minimumInventoryOneYear: rowData?.minimumInventoryOneYear || "",
        minimumPPE: rowData?.minimumPPE || "",
        minimumPPEThreeYear: rowData?.minimumPPEThreeYear || "",
        minimumPPETwoYear: rowData?.minimumPPETwoYear || "",
        minimumPPEOneYear: rowData?.minimumPPEOneYear || "",
        inventorySalesRatio: rowData?.inventorySalesRatio || "",
        profitLevelIndicatorMethod: rowData?.profitLevelIndicatorMethod || "OperatingMargin",
        iqrType: rowData?.iqrType || "IRS",
    };

    const {
        handleSubmit,
        register,
        formState: { errors },
        setValue,
        clearErrors,
        setError,
    } = useForm<FormFields>({ values: formValues });

    const { mutateAsync: updateBenchmarkMutation, isPending: isUpdatingBenchmark } = useUpdateBenchmarkInput();
    const { mutateAsync: createBenchmarkMutation, isPending: isCreatingBenchmark } = useCreateBenchmarkInput();
    const { mutateAsync: deleteBenchmarkMutation, isPending: isDeletingBenchmark } = useDeleteBenchmarkInput();
    const isSavingBenchmark = isUpdatingBenchmark || isCreatingBenchmark;

    const onSubmit: SubmitHandler<FormFields> = async data => {
        const benchmarkId = rowData?.id;
        const benchmarkArgs: CreateOrUpdateBenchmarkInput = {
            Id: benchmarkId,
            ClientId: clientId,
            Region: data.region as Region,
            ServiceType: data.serviceType,
            MinimumAverageTotalRevenue: data.minimumAverageTotalRevenue,
            MinimumThreeYearRevenue: data.minimumThreeYearRevenue,
            MinimumTwoYearRevenue: data.minimumTwoYearRevenue,
            MinimumOneYearRevenue: data.minimumOneYearRevenue,
            MinimumInventory: data.minimumInventory,
            MinimumInventoryThreeYear: data.minimumInventoryThreeYear,
            MinimumInventoryTwoYear: data.minimumInventoryTwoYear,
            MinimumInventoryOneYear: data.minimumInventoryOneYear,
            MinimumPPE: data.minimumPPE,
            MinimumPPEThreeYear: data.minimumPPEThreeYear,
            MinimumPPETwoYear: data.minimumPPETwoYear,
            MinimumPPEOneYear: data.minimumPPEOneYear,
            InventorySalesRatio: data.inventorySalesRatio,
            ProfitLevelIndicatorMethod: data.profitLevelIndicatorMethod,
            IqrType: data.iqrType,
        };

        if (isEdit) {
            await updateBenchmarkMutation({ data: benchmarkArgs, file: selectedBenchmarkFile });
        } else {
            if (!selectedBenchmarkFile) {
                setError("benchmarkFile" as const, {
                    type: "manual",
                    message: "Benchmark input file is required.",
                });
                return;
            }

            await createBenchmarkMutation({ benchmarkInput: benchmarkArgs, file: selectedBenchmarkFile });
        }
        onClose();
    };

    const handleRemoveBenchmarkFile = () => {
        setSelectedBenchmarkFile(undefined);
        setHasBenchmarkFile(false);
        setValue("benchmarkFile" as const, "");
        setError("benchmarkFile" as const, {
            type: "manual",
            message: "Benchmark input file is required",
        });
    };

    const handleDelete = async () => {
        if (rowData) {
            await deleteBenchmarkMutation(rowData?.id || "");
            onClose();
        }
    };

    const closeConfirmModal = () => {
        setShowConfirmModal(false);
    };

    const onFileAdded = async (fileContainer: FileContainer) => {
        const fileName = fileContainer.file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

        if (ALLOWED_BENCHMARK_EXTENSIONS.includes(fileExtension)) {
            setSelectedBenchmarkFile(fileContainer);
            setValue("benchmarkFile" as const, fileName);
            clearErrors("benchmarkFile" as const);
        } else {
            setSelectedBenchmarkFile(undefined);
            setValue("benchmarkFile" as const, "");
            setError("benchmarkFile" as const, {
                type: "manual",
                message: "Invalid file format.",
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    <Flex flexDirection="column" gap={4}>
                        <FormControl isRequired isInvalid={!!errors.region}>
                            <FormLabel>Region</FormLabel>
                            <Select {...register("region", { required: "Region is required" })} placeholder="Select region">
                                {REGIONS.map(region => (
                                    <option key={region} value={region}>
                                        {getRegionLabel(region)}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.serviceType}>
                            <FormLabel>Service Type</FormLabel>
                            <Select {...register("serviceType", { required: "Service type is required" })} placeholder="Select service type">
                                {SERVICE_TYPES.map(service => (
                                    <option key={service} value={service}>
                                        {service}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.serviceType?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.profitLevelIndicatorMethod}>
                            <FormLabel>Profit Level Indicator</FormLabel>
                            <Select
                                {...register("profitLevelIndicatorMethod", { required: "Profit Level Indicator is required" })}
                                placeholder="Select Profit Level Indicator"
                            >
                                {PROFIT_LEVEL_INDICATOR_METHODS.map(method => (
                                    <option key={method} value={method}>
                                        {getProfitLevelIndicatorMethodLabel(method)}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.profitLevelIndicatorMethod?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.iqrType}>
                            <FormLabel>Interquartile Range</FormLabel>
                            <Select {...register("iqrType", { required: "Interquartile range is required" })} placeholder="Select interquartile range">
                                {IQR_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.iqrType?.message}</FormErrorMessage>
                        </FormControl>
                        <Box mt="2rem">
                            <h3>Total Revenue</h3>
                            <FormControl isInvalid={!!errors.minimumAverageTotalRevenue}>
                                <Flex justify="space-between" align="center" gap={4}>
                                    <FormLabel mb={0}>Minimum three year average total revenue (in millions)</FormLabel>
                                    <InputGroup width="15rem" flexShrink={0}>
                                        <Input
                                            {...register("minimumAverageTotalRevenue", {
                                                valueAsNumber: true,
                                                validate: {
                                                    positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                },
                                            })}
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="1"
                                        />
                                    </InputGroup>
                                </Flex>
                                <FormErrorMessage>{errors.minimumAverageTotalRevenue?.message}</FormErrorMessage>
                            </FormControl>
                            <Box>
                                <FormLabel mt="2rem">Revenue (in millions) less than X over the last:</FormLabel>
                                <Flex gap="2rem">
                                    <FormControl isInvalid={!!errors.minimumThreeYearRevenue}>
                                        <FormLabel>Three Years</FormLabel>
                                        <InputGroup>
                                            <Input
                                                {...register("minimumThreeYearRevenue", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="1"
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumThreeYearRevenue?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumTwoYearRevenue}>
                                        <FormLabel>Two of the Last Three Years</FormLabel>
                                        <InputGroup>
                                            <Input
                                                {...register("minimumTwoYearRevenue", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="1"
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumTwoYearRevenue?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumOneYearRevenue}>
                                        <FormLabel>One of the Last Three Years</FormLabel>
                                        <InputGroup>
                                            <Input
                                                {...register("minimumOneYearRevenue", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="1"
                                            />
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumOneYearRevenue?.message}</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            </Box>
                        </Box>
                        <Box mt="2rem">
                            <h3>Inventory</h3>
                            <FormControl isInvalid={!!errors.minimumInventory}>
                                <Flex justify="space-between" align="center" gap={4}>
                                    <FormLabel>Minimum Inventory (%)</FormLabel>
                                    <InputGroup width="15rem" flexShrink={0}>
                                        <Input
                                            {...register("minimumInventory", {
                                                valueAsNumber: true,
                                                validate: {
                                                    positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    max: v => !v || parseFloat(v) <= 100 || "Cannot exceed 100%",
                                                },
                                            })}
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            placeholder="10"
                                        />
                                        <InputRightAddon>%</InputRightAddon>
                                    </InputGroup>
                                    <FormErrorMessage>{errors.minimumInventory?.message}</FormErrorMessage>
                                </Flex>
                            </FormControl>
                            <Box>
                                <FormLabel mt="2rem">Inventory / Sales ratio greater than X over the last:</FormLabel>
                                <Flex gap="2rem">
                                    <FormControl isInvalid={!!errors.minimumInventoryThreeYear}>
                                        <FormLabel>Three Years</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumInventoryThreeYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="10"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumInventoryThreeYear?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumInventoryTwoYear}>
                                        <FormLabel>Two Years</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumInventoryTwoYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="10"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumInventoryTwoYear?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumInventoryOneYear}>
                                        <FormLabel>One Year</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumInventoryOneYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="10"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumInventoryOneYear?.message}</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            </Box>
                            <FormControl mt="2rem" isInvalid={!!errors.inventorySalesRatio}>
                                <FormLabel>Inventory Sales Ratio (%)</FormLabel>
                                <InputGroup>
                                    <Input
                                        {...register("inventorySalesRatio", {
                                            valueAsNumber: true,
                                            validate: {
                                                positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                max: v => !v || parseFloat(v) <= 100 || "Cannot exceed 100%",
                                            },
                                        })}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        placeholder="50"
                                    />
                                    <InputRightAddon>%</InputRightAddon>
                                </InputGroup>
                                <FormErrorMessage>{errors.inventorySalesRatio?.message}</FormErrorMessage>
                            </FormControl>
                        </Box>
                        <Box mt="2rem">
                            <h3>PP&E</h3>
                            <FormControl isInvalid={!!errors.minimumPPE}>
                                <Flex justify="space-between" align="center" gap={4}>
                                    <FormLabel>Minimum PPE (%)</FormLabel>
                                    <InputGroup width="15rem" flexShrink={0}>
                                        <Input
                                            {...register("minimumPPE", {
                                                valueAsNumber: true,
                                                validate: {
                                                    positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    max: v => !v || parseFloat(v) <= 100 || "Cannot exceed 100%",
                                                },
                                            })}
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            placeholder="2"
                                        />
                                        <InputRightAddon>%</InputRightAddon>
                                    </InputGroup>
                                    <FormErrorMessage>{errors.minimumPPE?.message}</FormErrorMessage>
                                </Flex>
                            </FormControl>
                            <Box>
                                <FormLabel mt="2rem">Net PP&E / Sales ratio greater than X over the last:</FormLabel>
                                <Flex gap="2rem">
                                    <FormControl isInvalid={!!errors.minimumPPEThreeYear}>
                                        <FormLabel>Three Years</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumPPEThreeYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="2"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumPPEThreeYear?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumPPETwoYear}>
                                        <FormLabel>Two Years</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumPPETwoYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="2"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumPPETwoYear?.message}</FormErrorMessage>
                                    </FormControl>
                                    <FormControl isInvalid={!!errors.minimumPPEOneYear}>
                                        <FormLabel>One Year</FormLabel>
                                        <InputGroup flexShrink={0}>
                                            <Input
                                                {...register("minimumPPEOneYear", {
                                                    valueAsNumber: true,
                                                    validate: {
                                                        positive: v => !v || parseFloat(v) >= 0 || "Must be a positive value",
                                                    },
                                                })}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                placeholder="2"
                                            />
                                            <InputRightAddon>%</InputRightAddon>
                                        </InputGroup>
                                        <FormErrorMessage>{errors.minimumPPEOneYear?.message}</FormErrorMessage>
                                    </FormControl>
                                </Flex>
                            </Box>
                        </Box>
                        {hasBenchmarkFile ? (
                            <Box mt="2rem">
                                <FormLabel>Benchmark Input File:</FormLabel>
                                <Flex align="center" mt="1rem" mb="1rem">
                                    <p>{rowData?.blobStorageLink?.split("/").pop()}</p>
                                    <Tooltip label="Remove benchmark file">
                                        <CloseButton ml="1rem" variant="ghost" aria-label="Close" onClick={() => handleRemoveBenchmarkFile()} />
                                    </Tooltip>
                                </Flex>
                            </Box>
                        ) : (
                            <Box mt="2rem">
                                <FormControl isInvalid={!!errors?.benchmarkFile}>
                                    <FormLabel mt="2rem">Upload Benchmark Input:</FormLabel>
                                    <Flex direction="column" gap={5}>
                                        <FileUploader
                                            {...register("benchmarkFile")}
                                            maxSize={10485760}
                                            maxFiles={1}
                                            onFileAdded={onFileAdded}
                                            accept={{
                                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
                                            }}
                                        />
                                    </Flex>
                                    <FormErrorMessage>{errors.benchmarkFile?.message}</FormErrorMessage>
                                </FormControl>
                            </Box>
                        )}
                    </Flex>
                </Flex>
                <Flex gap="2rem" flexFlow="column nowrap" mt="2rem" mb="1rem">
                    <Flex w="100%" justify="space-between" align="center">
                        {isEdit ? (
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => setShowConfirmModal(true)}
                                isLoading={isDeletingBenchmark}
                                isDisabled={isDeletingBenchmark}
                            >
                                Delete Benchmark Input
                            </Button>
                        ) : (
                            <span />
                        )}
                        <Flex gap={2}>
                            <Button onClick={onClose} mr={3} variant="ghost">
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" isLoading={isSavingBenchmark} isDisabled={isSavingBenchmark}>
                                {rowData ? "Update" : "Add"}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            </form>
            <ConfirmModal
                show={showConfirmModal}
                handleDelete={handleDelete}
                onClose={closeConfirmModal}
                isDeletingEntity={isDeletingBenchmark}
                bodyText="Deleting this benchmark input will also delete any associated data."
            />
        </>
    );
};
